import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle, Plus, Trash, UploadSimple, Key } from "@phosphor-icons/react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { AvatarDisplay, MotifPicker } from "@/components/Avatar";
import { WriteTagButton } from "@/components/WriteTagButton";
import { useApp } from "@/context/AppContext";
import api from "@/lib/api";
import { copyText } from "@/lib/clipboard";
import { normalizePhone } from "@/lib/contactPicker";

export function CreateBuddyDialog({ open, onOpenChange, onCreated, prefill }) {
  const { t } = useApp();
  const navigate = useNavigate();
  const [label, setLabel] = useState("");
  const [useDefault, setUseDefault] = useState(true);
  const [allowCalls, setAllowCalls] = useState(true);
  const [allowMessages, setAllowMessages] = useState(true);
  const [phase, setPhase] = useState("form"); // form | success
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null);
  const [displayName, setDisplayName] = useState("");
  const [avatarType, setAvatarType] = useState("motif");
  const [avatarValue, setAvatarValue] = useState("paw");
  const [contacts, setContacts] = useState([]);
  const [notes, setNotes] = useState("");
  const fileRef = useRef();
  const [activationRequired, setActivationRequired] = useState(false);
  const [activationCredits, setActivationCredits] = useState(0);
  const [showCodeInput, setShowCodeInput] = useState(false);
  const [activationCode, setActivationCode] = useState("");

  useEffect(() => {
    if (open) {
      api.get("/activation/status").then((r) => { setActivationRequired(r.data.required); setActivationCredits(r.data.credits || 0); }).catch(() => {});
      if (prefill) {
        if (prefill.useDefault !== undefined) setUseDefault(prefill.useDefault);
        if (prefill.label) setLabel(prefill.label);
        if (prefill.displayName) setDisplayName(prefill.displayName);
        if (prefill.contacts) setContacts(prefill.contacts);
      }
    }
  }, [open]);

  const reset = () => { setLabel(""); setUseDefault(true); setAllowCalls(true); setAllowMessages(true); setPhase("form"); setBusy(false); setCreated(null); setDisplayName(""); setAvatarType("motif"); setAvatarValue("paw"); setContacts([]); setNotes(""); setActivationCode(""); setShowCodeInput(false); };
  const close = () => { onOpenChange(false); setTimeout(reset, 200); };

  const addContact = () => setContacts([...contacts, { id: crypto.randomUUID(), name: "", relationship: "", phone: "" }]);
  const contactsSupported = typeof navigator !== "undefined" && "contacts" in navigator && "ContactsManager" in window;
  const fromPhonebook = async () => {
    if (!contactsSupported) {
      toast.message("Die Kontaktauswahl wird auf diesem Gerät/Browser nicht unterstützt – bitte manuell eingeben.");
      addContact();
      return;
    }
    try {
      const r = await navigator.contacts.select(["name", "tel"], { multiple: false });
      if (r && r[0]) {
        const n = Array.isArray(r[0].name) ? r[0].name[0] : r[0].name;
        const p = normalizePhone(r[0].tel);
        setContacts((prev) => [...prev, { id: crypto.randomUUID(), name: n || "", relationship: "", phone: p || "" }]);
      }
    } catch { /* user cancelled or permission denied */ }
  };
  const updContact = (i, k, v) => { const c = [...contacts]; c[i] = { ...c[i], [k]: v }; setContacts(c); };
  const rmContact = (i) => setContacts(contacts.filter((_, idx) => idx !== i));
  const onUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await api.post("/profile/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setAvatarType("upload"); setAvatarValue(res.data.path);
      toast.success("Bild hochgeladen");
    } catch { toast.error("Upload fehlgeschlagen"); }
  };

  const run = async () => {
    setBusy(true);
    try {
      const payload = { label, use_default_profile: useDefault, allow_calls: allowCalls, allow_messages: allowMessages };
      if (activationRequired) { payload.activation_code = activationCode; }
      if (!useDefault) { payload.display_name = displayName; payload.avatar_type = avatarType; payload.avatar_value = avatarValue; payload.contacts = contacts; payload.notes = notes; }
      const res = await api.post("/tags/create", payload);
      const url = `${window.location.origin}/t/${res.data.code}`;
      await copyText(url);
      onCreated && onCreated();
      window.dispatchEvent(new Event("sb:activation-changed"));
      toast.success(t("createSuccess"));
      setCreated({ id: res.data.id, code: res.data.code, label });
      setPhase("success");
      setBusy(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Fehler"); setBusy(false); }
  };

  const noActivations = activationRequired && activationCredits <= 0 && !showCodeInput;

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? onOpenChange(true) : close())}>
      <DialogContent data-testid="create-buddy-dialog" className="max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading">{noActivations ? t("noActivationsTitle") : t("createBuddy")}</DialogTitle>
        </DialogHeader>

        {phase === "form" && noActivations && (
          <div className="space-y-4 text-center" data-testid="no-activations-modal">
            <div className="w-14 h-14 mx-auto rounded-2xl flex items-center justify-center" style={{ background: "color-mix(in srgb, var(--app-primary) 18%, transparent)" }}>
              <Key size={30} weight="duotone" style={{ color: "var(--app-strong)" }} />
            </div>
            <p className="font-body text-sm text-gray-600">{t("noActivationsText")}</p>
            <div className="space-y-2">
              <button data-testid="no-act-buy" onClick={() => { close(); navigate("/shop?buy=activation"); }} className="sb-btn w-full py-3 text-white font-medium" style={{ background: "var(--app-primary)" }}>{t("buyActivation")}</button>
              <button data-testid="no-act-havecode" onClick={() => setShowCodeInput(true)} className="sb-btn w-full py-3 bg-white font-medium">{t("haveCodeBtn")}</button>
            </div>
          </div>
        )}

        {phase === "form" && !noActivations && (
          <div className="space-y-4">
            {activationRequired && activationCredits > 0 && !showCodeInput && (
              <div data-testid="activation-credits-hint" className="rounded-2xl border-2 border-gray-800 bg-white p-3 flex items-center gap-2">
                <Key size={20} weight="duotone" style={{ color: "var(--app-strong)" }} />
                <p className="text-sm font-body text-gray-700"><b>{activationCredits}</b> {t("activationsAvailableWord")} · {t("activationsInfoHint")}</p>
              </div>
            )}
            {activationRequired && showCodeInput && (
              <div data-testid="activation-code-block" className="rounded-2xl border-2 border-gray-800 bg-white p-3">
                <label className="text-xs font-body text-gray-500 uppercase tracking-wider">{t("activationCodeLabel")}</label>
                <input data-testid="activation-code-input" className="sb-input mt-1" placeholder="SBXXXX-XXXX"
                  value={activationCode} onChange={(e) => setActivationCode(e.target.value)} />
                <p className="text-xs font-body text-gray-500 mt-1">{t("activationCodeHint")}</p>
              </div>
            )}
            <input data-testid="new-buddy-label" className="sb-input" placeholder={t("buddyNamePlaceholder")} value={label} onChange={(e) => setLabel(e.target.value)} />
            <div>
              <p className="text-xs font-body text-gray-500 uppercase tracking-wider mb-2">{t("dataSource")}</p>
              <div className="grid grid-cols-1 gap-2">
                <button type="button" data-testid="buddy-source-default" onClick={() => setUseDefault(true)}
                  className={`w-full text-left p-3 rounded-xl border-2 border-gray-800 ${useDefault ? "text-white" : "bg-white text-gray-800"}`} style={useDefault ? { background: "var(--app-primary)" } : {}}>
                  <span className="font-heading font-medium text-sm block">{t("sourceDefaultTitle")}</span>
                  <span className={`text-xs font-body block mt-0.5 ${useDefault ? "text-white/90" : "text-gray-500"}`}>{t("sourceDefaultDesc")}</span>
                </button>
                <button type="button" data-testid="buddy-source-custom" onClick={() => setUseDefault(false)}
                  className={`w-full text-left p-3 rounded-xl border-2 border-gray-800 ${!useDefault ? "text-white" : "bg-white text-gray-800"}`} style={!useDefault ? { background: "var(--app-primary)" } : {}}>
                  <span className="font-heading font-medium text-sm block">{t("sourceCustomTitle")}</span>
                  <span className={`text-xs font-body block mt-0.5 ${!useDefault ? "text-white/90" : "text-gray-500"}`}>{t("sourceCustomDesc")}</span>
                </button>
              </div>
            </div>

            {!useDefault && (
              <div className="space-y-4 pt-1" data-testid="create-custom-fields">
                <div className="flex items-center gap-3">
                  <AvatarDisplay type={avatarType} value={avatarValue} size={56} />
                  <div className="flex-1">
                    <label className="text-xs font-body text-gray-500 uppercase tracking-wider">{t("displayName")}</label>
                    <input data-testid="create-displayname" className="sb-input mt-1" placeholder={t("displayName")} value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
                  </div>
                </div>
                <div>
                  <p className="text-xs font-body text-gray-500 uppercase tracking-wider mb-2">{t("buddyAvatar")}</p>
                  <MotifPicker selectedType={avatarType} selectedValue={avatarValue} onSelect={(type, val) => { setAvatarType(type); setAvatarValue(val); }} onUpload={onUpload} testidPrefix="create-motif" />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-body text-gray-500 uppercase tracking-wider">{t("emergencyContacts")}</p>
                    <div className="flex items-center gap-2">
                      <button type="button" data-testid="create-phonebook" onClick={fromPhonebook} className="sb-btn px-2 py-1 bg-white text-xs">{t("fromPhonebook")}</button>
                      <button type="button" data-testid="create-add-contact" onClick={addContact} className="p-1.5 rounded-lg border-2 border-gray-800 text-white active:scale-90" style={{ background: "var(--app-primary)" }}><Plus size={16} weight="bold" /></button>
                    </div>
                  </div>
                  <div className="space-y-3">
                    {contacts.map((c, i) => (
                      <div key={c.id || i} className="space-y-2 pb-3 border-b border-gray-200 last:border-0">
                        <div className="flex gap-2">
                          <input className="sb-input" placeholder={t("name")} value={c.name} onChange={(e) => updContact(i, "name", e.target.value)} data-testid={`create-contact-name-${i}`} />
                          <button type="button" onClick={() => rmContact(i)} className="p-3 rounded-xl border-2 border-gray-800 bg-white text-red-500 active:scale-90"><Trash size={18} weight="bold" /></button>
                        </div>
                        <input className="sb-input" placeholder={t("relationship")} value={c.relationship} onChange={(e) => updContact(i, "relationship", e.target.value)} data-testid={`create-contact-rel-${i}`} />
                        <input className="sb-input" placeholder={t("phone")} value={c.phone} onChange={(e) => updContact(i, "phone", e.target.value)} data-testid={`create-contact-phone-${i}`} />
                        <p className="text-xs font-body text-gray-400">{t("contactPhoneHint")}</p>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-xs font-body text-gray-500 uppercase tracking-wider">{t("notes")}</label>
                  <textarea data-testid="create-notes" className="sb-input mt-1" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
                </div>
              </div>
            )}
            <div className="space-y-2">
              <div className="flex items-center justify-between"><span className="font-body text-sm">{t("allowCalls")}</span><Switch data-testid="buddy-allow-calls" checked={allowCalls} onCheckedChange={setAllowCalls} /></div>
              <div className="flex items-center justify-between"><span className="font-body text-sm">{t("allowMessages")}</span><Switch data-testid="buddy-allow-messages" checked={allowMessages} onCheckedChange={setAllowMessages} /></div>
            </div>
            <button data-testid="confirm-create-buddy" onClick={() => { if (activationRequired && showCodeInput && !activationCode.trim()) { toast.error(t("activationCodeRequired")); return; } run(); }} disabled={busy} className="sb-btn w-full py-3.5 text-white font-medium disabled:opacity-50" style={{ background: "var(--app-primary)" }}>{busy ? t("creating") : t("create")}</button>
          </div>
        )}

        {phase === "success" && created && (
          <div className="space-y-4 text-center" data-testid="create-success">
            <CheckCircle size={64} weight="fill" className="mx-auto" style={{ color: "#22C55E" }} />
            <div>
              <p className="font-heading font-semibold text-lg text-gray-900">{t("createdTitle")}</p>
              <p className="font-body text-sm text-gray-600 mt-1">{created.label || t("unnamed")} · /t/{created.code}</p>
            </div>
            <WriteTagButton tag={created} label={t("writeTagNow")}
              className="sb-btn w-full py-3.5 text-white font-medium flex items-center justify-center gap-2" />
            <button data-testid="create-success-done" onClick={close} className="sb-btn w-full py-3 bg-white font-medium">{t("done")}</button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
